import CardAdd from "@assets/icons/card-add.svg";
import Plus from "@assets/icons/plus.svg";
import { AddDetailModal } from "@components/add-detail-modal";
import { Block } from "@components/block";
import { Button } from "@components/button";
import { FilterConfig } from "@components/filter-modal";
import { StatusBlock, StatusBlockType } from "@components/status-block";
import { Config, Table } from "@components/table";
import { useRequisites } from "@features/requisite";
import { Requisite, RequisiteForm } from "@features/requisite/api";
import { useBoolState } from "@hooks/use-bool-state";
import { usePagination } from "@hooks/use-pagination";
import { useUrlFilters } from "@hooks/useUrlFilters";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./details.module.scss";
import { EllipsisText } from "@components/ellipsis-text";
import { SortingConfig } from "@components/table/sorting";
import { useWebSocket } from "@request/useWebsocket";
import { WebsocketUpdateType } from "@request/websocket";
// import { useAppSelector } from "@app/store";

type RequisiteFilter = RequisiteForm & {
  status?: string;
};

export const DetailsPage = () => {
  // const allowRequisitesWithoutDevice = useAppSelector(
  //   (state) => state.auth.allowRequisitesWithoutDevice
  // );
  const config: Config<Requisite> = [
    { label: "ФИО", source: "full_name" },
    {
      label: "Номер карты/cчета",
      source: "card_number",
      renderComponent: (data) => <EllipsisText text={data} />,
    },
    { label: "Метод", source: "type" },
    { label: "Банк", source: "bank", renderComponent: (bank) => bank.name },
    { label: "Телефон", source: "phone_number" },
    {
      label: "Устройство",
      source: "device_name",
    },
    {
      label: "Оборот",
      source: "balance",
      renderComponent: (sumIncome) => `${sumIncome}\xa0₽`,
    },
    {
      label: "Конверсия",
      source: "conversion",
      renderComponent: (conversion) =>
        `${Math.round((conversion || 0) * 100) / 100} %`,
    },
    {
      label: "Статус",
      source: "active",
      renderComponent: (active, item) => {
        if (item.deleted) {
          return <StatusBlock text="В архиве" type={StatusBlockType.Error} />;
        }

        let activeStatus;

        if (!active) {
          activeStatus = (
            <StatusBlock
              text="Не проверен"
              type={StatusBlockType.Error}
              hint={item.scheduler_tooltip || "Необходима проверка реквизита"}
            />
          );
        } else if (!item.at_work) {
          activeStatus = (
            <StatusBlock
              text="Не активен"
              type={StatusBlockType.Warning}
              hint={
                item.scheduler_tooltip || "Необходимо включить реквизит вручную"
              }
            />
          );
        } else if (!item.scheduled_active) {
          activeStatus = (
            <StatusBlock
              text="Активен"
              type={StatusBlockType.Orange}
              hint={item.scheduler_tooltip || "Реквизит активен по расписанию"}
            />
          );
        } else {
          activeStatus = (
            <StatusBlock
              text="В работе"
              type={StatusBlockType.Success}
              hint={item.scheduler_tooltip}
            />
          );
        }

        return <>{activeStatus}</>;
      },
    },
  ];
  const navigate = useNavigate();

  const [isModalOpen, showModal, hideModal] = useBoolState(false);

  const { state, getRequisites } = useRequisites();
  const { page, limit, setPage, setLimit } = usePagination(100);
  const { setParams } = useUrlFilters();
  // Create a ref to hold the latest filters
  const paramsRef = useRef({} as RequisiteForm);
  const { lastMessage } = useWebSocket();

  useEffect(() => {
    if (lastMessage && lastMessage.type == WebsocketUpdateType.Requisite) {
      getRequisites({ ...paramsRef.current, page, limit });
    }
  }, [lastMessage, limit, page]);

  useEffect(() => {
    getRequisites({page: 1, limit: 100})
  }, [])
  // // Modify the useEffect to include filters in periodic fetches
  // useEffect(() => {
  //   // Define a function to fetch data with the latest filters
  //   const fetchData = () => {
  //     getRequisites({ ...paramsRef.current, page, limit });
  //   };

  //   // Fetch data immediately when page or limit changes
  //   fetchData();

  //   // Set up an interval to fetch data every 30 seconds with the latest filters
  //   const intervalId = setInterval(fetchData, 30000);

  //   // Clean up the interval when the component unmounts or dependencies change
  //   return () => clearInterval(intervalId);
  // }, [getRequisites, page, limit]);

  const filterConfig: FilterConfig<RequisiteFilter> = [
    {
      type: "select",
      options: [
        { text: "Не проверен", value: "Не проверен" },
        { text: "Не активен", value: "Не активен" },
        { text: "Активен", value: "Активен" },
        { text: "В работе", value: "В работе" },
      ],
      name: "status",
      label: "Статус",
    },
    {
      type: "select",
      options: [
        { text: "В архиве", value: "true" },
        { text: "Не в архиве", value: "false" },
      ],
      name: "deleted",
      label: "Архив",
    },
    {
      type: "sum",
      minName: "min_amount",
      maxName: "max_amount",
    },
    {
      type: "date",
      withTime: true,
      nameStart: "start_date",
      nameEnd: "end_date",
      returnValue: "string",
    },
  ];
  const onSubmit = (data: RequisiteFilter) => {
    if (data.status) {
      switch (data.status) {
        case "Не проверен":
          data.active = false;
          break;
        case "Не активен":
          data.active = true;
          data.at_work = false;
          break;
        case "Активен":
          data.active = true;
          data.at_work = true;
          data.scheduled_active = false;
          break;
        case "В работе":
          data.active = true;
          data.at_work = true;
          data.scheduled_active = true;
          break;
      }
    }
    data.status = undefined;
    setParams(data);
    paramsRef.current = data;
    getRequisites({ ...data, page, limit });
  };

  const onSortChange = (sort: { type: string; direction: string }) => {
    paramsRef.current.sort_by = sort.type;
    paramsRef.current.sort_desc = sort.direction == "desc";
    getRequisites({ ...paramsRef.current, page, limit });
  };

  const sortingConfig: SortingConfig = {
    onSortChange,
    items: [
      {
        name: "priority",
        typeLabel: "По активности",
        labels: [
          { type: "desc", label: "Сверху активные" },
          { type: "asc", label: "Сверху неактивные" },
        ],
      },
      {
        name: "balance",
        typeLabel: "По обороту",
        labels: [
          { type: "desc", label: "От большего к меньшему" },
          { type: "asc", label: "От меньшего к большему" },
        ],
      },
      {
        name: "conversion",
        typeLabel: "По конверсии",
        labels: [
          { type: "desc", label: "От большего к меньшему" },
          { type: "asc", label: "От меньшего к большему" },
        ],
      },
    ],
  };

  return (
    <>
      <div className={styles.details}>
        <div className={styles.top}>
          <Block className={styles.card}>
            <div>
              <h3 className={styles.title}>Новые реквизиты</h3>
              <Button icon={Plus} onClick={showModal}>
                Добавить
              </Button>
            </div>
            <div className={styles.icon}>
              <CardAdd />
            </div>
          </Block>
        </div>

        <Block>
          <Table
            className={styles.table}
            cellClassName={styles.tableCell}
            title="Реквизиты"
            placeholder="Поиск по реквизитам"
            withFilter // !TODO ask
            filterProps={{
              filterConfig: filterConfig,
              // validationRules: validationRules,
              onSubmit: onSubmit,
            }}
            config={config}
            onRowClick={(item) => {
              navigate(`${item.id}`);
            }}
            data={state.requisites}
            loading={state.loading}
            setPage={setPage}
            setLimit={setLimit}
            limits={[100, 200, 300]}
            page={page}
            limit={limit}
            totalCount={state.totalCount}
            sortingConfig={sortingConfig}
          />
        </Block>
      </div>
      <AddDetailModal isOpen={isModalOpen} hide={hideModal} />
    </>
  );
};
